import { z } from 'zod';
import { defineCollection, type RecordOf } from '../collection.ts';
import { cents, currencyCode, id, isoDate, longText, provenance, relatedRefs, requiredText, shortText, tagList } from '../fields.ts';

export const DOCUMENT_CATEGORIES = [
  'insurance',
  'warranty',
  'school',
  'medical',
  'pet',
  'vehicle',
  'home',
  'manual',
  'receipt',
  'identity',
  'financial',
  'legal',
  'other',
] as const;
export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number];

export const docFolders = defineCollection({
  name: 'doc_folders',
  module: 'vault',
  label: { one: 'folder', other: 'folders' },
  schema: z.object({
    name: requiredText(100),
    description: shortText(500).default(''),
  }),
  defaultAudience: 'adults',
  childAccess: 'read',
  editPolicy: 'collaborative',
  immutableFields: [],
  manualMergeFields: [],
});

/**
 * A stored household document. Folders organize; they do not grant access —
 * each document carries its own audience (default: grown-ups only).
 */
export const documents = defineCollection({
  name: 'documents',
  module: 'vault',
  label: { one: 'document', other: 'documents' },
  schema: z.object({
    title: requiredText(200),
    folderId: id.nullable().default(null),
    fileId: id,
    fileName: shortText(255),
    contentType: shortText(100),
    sizeBytes: z.number().int().min(0),
    category: z.enum(DOCUMENT_CATEGORIES).default('other'),
    tags: tagList.default([]),
    notes: longText(10_000).default(''),
    /** Renewal/expiry date, surfaced on the home screen when it approaches. */
    expiresOn: isoDate.nullable().default(null),
    /** Manually entered receipt details (no OCR). */
    receipt: z
      .object({
        vendor: shortText(200).default(''),
        date: isoDate.nullable().default(null),
        amountCents: cents.nullable().default(null),
        currency: currencyCode.default('USD'),
      })
      .nullable()
      .default(null),
    related: relatedRefs.default([]),
    source: provenance.nullable().default(null),
  }),
  defaultAudience: 'adults',
  childAccess: 'read',
  editPolicy: 'collaborative',
  immutableFields: ['fileId', 'contentType', 'sizeBytes', 'source'],
  manualMergeFields: ['notes'],
});

export type DocFolder = RecordOf<typeof docFolders>;
export type VaultDocument = RecordOf<typeof documents>;
